import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * The standard dialog header: title, optional supporting line, optional close button.
 *
 * There was no shared one before this, and the app had grown six incompatible copies of the
 * same shape - three built on `mat-card-header` inside a dialog, one nesting the close button
 * inside the `<h1 mat-dialog-title>` (which puts the button into the dialog's accessible name),
 * and two hand-rolled `div.dialog-header`s. Reach for this instead of writing a seventh.
 *
 * Text arrives already resolved - callers use the translate pipe at the call site - so this
 * stays i18n-agnostic and nothing needs a `translateContent` flag to opt back out.
 *
 * Deliberately NOT `mat-dialog-title`. That directive's `margin: 0 0 20px` is injected into
 * <head> at runtime by a `ViewEncapsulation.None` component, so it lands after every
 * build-time stylesheet at equal specificity and a Tailwind margin utility cannot beat it.
 * Pass `titleId` here and the same value as the dialog's `ariaLabelledBy` instead, which gets
 * the same accessible name with no override to maintain.
 */
@Component({
  selector: 'app-dialog-header',
  templateUrl: './dialog-header.component.html',
})
export class DialogHeaderComponent {
  @Input() title = '';

  /** Supporting line under the title. Hidden when empty. */
  @Input() subtitle = '';

  /**
   * Id for the heading element. Pass the same string as the dialog's `ariaLabelledBy` so the
   * dialog is announced by its title. Not auto-generated: `ariaLabelledBy` has to be set at
   * `MatDialog.open()` time, before this component exists to hand an id back.
   */
  @Input() titleId = '';

  @Input() showClose = true;

  /** For a dialog that must not be dismissed mid-flight, e.g. while a bulk save is running. */
  @Input() closeDisabled = false;

  /** Accessible name and tooltip for the close button. Callers pass `'General.Close' | translate`. */
  @Input() closeLabel = '';

  /** A rule under the header. The `mat-card` dialogs draw one; the affiliate picker does not. */
  @Input() divider = false;

  /** Drops the bottom margin, for a host that frames the header itself (its own padding and rule). */
  @Input() flush = false;

  @Output() closed = new EventEmitter<void>();
}
